'use client'

import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import Image from 'next/image'
import { toPhotoUrl, type FulfillmentApi } from '@entities/fulfillment'
import { Button } from '@ui/base/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { Item, ItemActions, ItemButton, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@ui/base/item'
import { cn } from '@utils/cn'
import { ImagePlusIcon } from 'lucide-react'

const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
const maxSize = 5 * 1024 * 1024

export const maxPhotoCount = 7

export const photoIsAllowed = (file: File) => allowedTypes.includes(file.type) && file.size <= maxSize

const inputId = 'fulfillment-photos'

/** Имя кнопки-зоны: то же, что видно в ней заголовком. */
const pickerTitle = 'Перетащите фотографии сюда или выберите на диске'

type PhotosFieldProps = {
  /** Фотографии, уже лежащие на сервере: у новой карточки их ещё нет. */
  savedPhotos: FulfillmentApi.Photo[]
  /** Выбранные, но ещё не отправленные файлы. */
  files: File[]
  error: string
  onChange: (files: File[]) => void
  onRemoveSaved: (photoId: string) => void
}

export const PhotosField = ({ savedPhotos, files, error, onChange, onRemoveSaved }: PhotosFieldProps) => {
  const input = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const previews = useMemo(() => files.map((file) => URL.createObjectURL(file)), [files])
  const freeSlots = maxPhotoCount - savedPhotos.length - files.length

  // Браузер держит файл в памяти, пока ссылку не освободили: снимаем её при смене списка и при уходе со страницы.
  useEffect(
    () => () => {
      for (const preview of previews) {
        URL.revokeObjectURL(preview)
      }
    },
    [previews]
  )

  // Поле очищаем перед открытием: иначе повторный выбор тех же файлов не даёт события change.
  const openPicker = () => {
    if (input.current) {
      input.current.value = ''
      input.current.click()
    }
  }

  const addFiles = (selected: FileList | null) => {
    if (selected) {
      onChange([...files, ...Array.from(selected)])
    }
  }

  // Уже загруженные и только что выбранные фотографии показываем одним списком, по порядку.
  const rows = [
    ...savedPhotos.map((photo) => ({
      key: photo.id,
      src: toPhotoUrl(photo.url),
      title: `Фото ${photo.position + 1}`,
      remove: () => onRemoveSaved(photo.id),
    })),
    ...files.map((file, index) => ({
      key: `${file.name}-${index}`,
      src: previews[index],
      title: file.name,
      remove: () => onChange(files.filter((_, position) => position !== index)),
    })),
  ]

  return (
    <Field data-invalid={Boolean(error)}>
      <FieldLabel htmlFor={inputId}>Фотографии склада</FieldLabel>
      <Input
        ref={input}
        id={inputId}
        type="file"
        multiple
        accept={allowedTypes.join(',')}
        className="sr-only"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${inputId}-error` : undefined}
        onChange={(event) => addFiles(event.target.files)}
      />

      {rows.map((row) => (
        <Item key={row.key} variant="outline" size="sm">
          <ItemMedia variant="image" className="size-10">
            {row.src && <Image src={row.src} alt="" width={80} height={80} unoptimized />}
          </ItemMedia>
          <ItemContent className="flex-none">
            <ItemTitle>{row.title}</ItemTitle>
          </ItemContent>
          <ItemActions>
            <Button type="button" variant="ghost" size="sm" onClick={row.remove}>
              Убрать
            </Button>
          </ItemActions>
        </Item>
      ))}

      {freeSlots > 0 && (
        // Зона выбора — настоящая кнопка: до неё нужно доходить с клавиатуры, а не только мышью.
        // Имя кнопки задано отдельно, иначе программа чтения с экрана прочитала бы вместе с ним и
        // требования к файлам — они остаются пояснением, которое звучит после имени.
        <Item
          render={<ItemButton />}
          variant="outline"
          size="sm"
          aria-label={pickerTitle}
          aria-describedby={`${inputId}-requirements`}
          className={cn(
            'w-full cursor-pointer flex-col items-center gap-3 border-dashed py-8 text-center',
            dragging && 'border-primary bg-accent/40'
          )}
          onClick={openPicker}
          onDragOver={(event: DragEvent<HTMLDivElement>) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event: DragEvent<HTMLDivElement>) => {
            event.preventDefault()
            setDragging(false)
            addFiles(event.dataTransfer.files)
          }}
        >
          <ItemMedia variant="accent">
            <ImagePlusIcon />
          </ItemMedia>
          <ItemContent className="flex-none items-center">
            <ItemTitle>{pickerTitle}</ItemTitle>
            {/* Требования к файлам стоят внутри зоны, как у главного фото: рядом обе зоны читаются
                одинаково и не расходятся по высоте. */}
            <ItemDescription id={`${inputId}-requirements`}>
              JPG, PNG или WebP, не больше 5 МБ каждая. Ещё можно добавить {freeSlots} из {maxPhotoCount}.
            </ItemDescription>
          </ItemContent>
        </Item>
      )}

      {freeSlots === 0 && (
        <FieldDescription>
          Добавлено {maxPhotoCount} из {maxPhotoCount} — это предел.
        </FieldDescription>
      )}
      <FieldError id={`${inputId}-error`}>{error}</FieldError>
    </Field>
  )
}
